/**
 * <icon-tabs-slideshow> — thin tab controller around the theme's native
 * <effect-carousel>.
 *
 * The carousel itself (crossfade, autoplay, swipe, prev/next buttons) is handled
 * entirely by the theme's EffectCarousel. This element only wires the icon tabs
 * to it: clicking / keyboarding a tab selects the matching slide, and the tab
 * states are kept in sync with the carousel's own selection events.
 */
class IconTabsSlideshow extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector('effect-carousel');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

    if (!this.carousel || this.tabs.length === 0) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this._select(index));
      tab.addEventListener('keydown', (event) => this._onKeydown(event, index));
    });

    // The carousel emits carousel:select whenever the active slide changes —
    // whether from a tab, an arrow, a swipe or autoplay. Mirror it onto the tabs.
    this.carousel.addEventListener('carousel:select', (event) => {
      this._syncTabs(event.detail.index);
    });
  }

  _select(index) {
    if (typeof this.carousel.select === 'function') {
      this.carousel.select(index);
    } else {
      // Fallback if the carousel has not upgraded yet: use the theme's control event.
      this.carousel.dispatchEvent(
        new CustomEvent('control:select', { detail: { index } })
      );
    }
  }

  _syncTabs(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
  }

  _onKeydown(event, index) {
    let target = null;

    switch (event.key) {
      case 'ArrowLeft':
        target = (index - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'ArrowRight':
        target = (index + 1) % this.tabs.length;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.tabs[target].focus();
    this._select(target);
  }
}

if (!window.customElements.get('icon-tabs-slideshow')) {
  window.customElements.define('icon-tabs-slideshow', IconTabsSlideshow);
}
